// The galaxy map screen (GDD 08 screen 1, spec 15c): the Pixi map plus its HTML overlays.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router';
import { useFleets, useMap, useVisibleFleets } from '../api/queries';
import type { GameContext } from '../components/GameLayout';
import { FleetCard } from '../components/map/FleetCard';
import { MapControls } from '../components/map/MapControls';
import { MapLegend } from '../components/map/MapLegend';
import { MapSearch } from '../components/map/MapSearch';
import { SystemCard } from '../components/map/SystemCard';
import { ErrorNotice, Spinner } from '../components/ui';
import { fleetMarker, mapFleets } from '../map/fleets';
import { GalaxyMap, type GalaxyMapHandle, type MapSelection } from '../map/GalaxyMap';
import { stationedShips } from '../map/garrisons';
import { useNow } from '../state/live';
import { useSelectedSystem } from '../state/selectedSystem';
import { useSystemNames } from './fleet';

/** Zoom factor of the + and − buttons. */
const ZOOM_STEP = 1.5;

export function GalaxyMapScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const map = useMap(galaxyId);
  const ownFleets = useFleets(galaxyId);
  const [selection, setSelection] = useState<MapSelection>(null);
  const select = useSelectedSystem((s) => s.select);
  const mapRef = useRef<GalaxyMapHandle>(null);
  const visible = useVisibleFleets(galaxyId);
  const systemName = useSystemNames(galaxyId);
  const now = useNow();
  const [params, setParams] = useSearchParams();

  const ownSystemIds = useMemo(() => new Set(me?.systems.map((s) => s.id) ?? []), [me?.systems]);
  const homeSystemId = me?.systems[0]?.id ?? null;
  const garrisons = useMemo(
    () => stationedShips(ownFleets.data?.items ?? [], ownSystemIds),
    [ownFleets.data, ownSystemIds]
  );
  const points = useMemo(() => new Map(map.data?.systems.map((s) => [s.id, s]) ?? []), [map.data]);
  const fleets = useMemo(
    () => mapFleets(visible.data?.items ?? [], points),
    [visible.data, points]
  );
  const selected = selection?.kind === 'system' ? points.get(selection.id) : undefined;
  const selectedFleet =
    selection?.kind === 'fleet' ? fleets.find((f) => f.id === selection.id) : undefined;

  const focusSystem = useCallback(
    (id: string) => {
      const system = points.get(id);
      if (!system) return;
      mapRef.current?.focus(system);
      setSelection({ kind: 'system', id });
      select(galaxyId, id);
    },
    [points, select, galaxyId]
  );

  // `?system=<id>` / `?fleet=<id>` (from "Show on map"): focus and select once, then drop the param.
  useEffect(() => {
    const systemId = params.get('system');
    const fleetId = params.get('fleet');
    if (systemId && points.size > 0) {
      focusSystem(systemId);
      setParams({}, { replace: true });
    } else if (fleetId && visible.data && points.size > 0) {
      const fleet = fleets.find((f) => f.id === fleetId);
      if (fleet) {
        mapRef.current?.focus(fleetMarker(fleet, Date.now()).point);
        setSelection({ kind: 'fleet', id: fleetId });
      }
      setParams({}, { replace: true });
    }
  }, [params, points, fleets, visible.data, focusSystem, setParams]);

  if (map.isPending) return <Spinner label="Charting the galaxy…" />;
  if (map.error) return <ErrorNotice error={map.error} />;

  return (
    <div className="absolute inset-0" data-map-fleets={fleets.length}>
      <GalaxyMap
        ref={mapRef}
        map={map.data}
        playerId={me?.playerId}
        allianceId={me?.alliance?.id}
        homeSystemId={homeSystemId}
        ownSystemIds={ownSystemIds}
        garrisons={garrisons}
        fleets={fleets}
        selection={selection}
        onSelect={(next) => {
          setSelection(next);
          if (next?.kind === 'system') select(galaxyId, next.id);
        }}
      />
      <MapLegend hasAlliance={Boolean(me?.alliance)} />
      <MapSearch
        systems={map.data.systems}
        onPick={(r) => focusSystem(r.kind === 'system' ? r.systemId : r.firstSystemId)}
      />
      <MapControls
        onHome={homeSystemId ? () => focusSystem(homeSystemId) : undefined}
        onZoomIn={() => mapRef.current?.zoomBy(ZOOM_STEP)}
        onZoomOut={() => mapRef.current?.zoomBy(1 / ZOOM_STEP)}
        onFit={() => mapRef.current?.fit()}
      />
      {selected ? (
        <SystemCard
          galaxyId={galaxyId}
          system={selected}
          me={me}
          isHome={selected.id === homeSystemId}
          onClose={() => setSelection(null)}
        />
      ) : selectedFleet ? (
        <FleetCard
          galaxyId={galaxyId}
          fleet={selectedFleet}
          now={now}
          systemName={systemName}
          onClose={() => setSelection(null)}
        />
      ) : null}
    </div>
  );
}
